// #region imports

// react
import React, { useState, useEffect } from 'react';

// custom components
import { AddToCartButton } from '_components/atoms';
import CommentModal from './comment-modal';
import CustomQuantityClicker from './custom-quantity-clicker';
import Comment from './comment';

// styles
import { Colors, Spacing } from '_styles';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectShoppingCart, removeFromCart } from '_redux/slices/marketplace/user-marketplace';

// #endregion

const ProductModal = ({ product, onClose, modalOpen, origin }) => {

    // #region redux
    const dispatch = useDispatch();
    const shoppingCart = useSelector(selectShoppingCart);
    // #endregion

    // #region local states
    const [showInput, setShowInput] = useState(false);
    const [comment, setComment] = useState('');
    const [quantity, setQuantity] = useState(1);
    // #endregion


    // #region use effects

    // initialise quantity from shopping cart if product exists
    useEffect(() => {
        if (product?.id && shoppingCart?.[product.id]?.quantity != null) {
            setQuantity(shoppingCart[product.id].quantity);
        }
    }, [shoppingCart, product]);

    // #endregion


    // #region handlers

    const handleSubtract = () => {
        if (quantity === 1) {
            dispatch(removeFromCart(product.id));
            setQuantity(0);
            onClose();
        }
        else {
            setQuantity(quantity - 1);
        }
    }

    // #endregion

    return (
        <div style={styles.container}>
            <div>
                {/* Product Title */}
                <div style={styles.header}>
                    <button onClick={onClose} style={styles.closeButton}>&times;</button>
                </div>

                {/* Product Image */}
                <div style={styles.imageContainer}>
                    <img src={product?.image} alt={product?.productTitle} style={styles.image} />
                </div>

                {/* Product Price */}
                <div style={styles.titlePriceRow}>
                    <h5 style={styles.title}>{product?.productTitle || "Product"}</h5>
                    <p style={styles.price}>€{product?.price?.toFixed(2) || '0.00'}</p>
                </div>

                {/* Product Description */}
                <p style={styles.description}>{product?.description || "No description"}</p>

                <div style={{ display: 'flex', flex: 1 }}>
                    {/* quantity clicker */}
                    <CustomQuantityClicker
                        quantity={quantity}
                        subtract={handleSubtract} 
                        add={() => { setQuantity(quantity + 1) }}
                        minusDisabled={quantity <= 0}
                        plusDisabled={quantity >= 10}
                    />
                </div>

                {/* Add / Edit Comment */}
                <Comment
                    comment={comment}
                    setComment={setComment}
                    setShowInput={setShowInput}
                    origin={origin}
                />

            </div>

            {/* add to cart button */}
            <div>
                <div style={styles.actionRow}>
                    <div>
                        <AddToCartButton
                            price={product?.price}
                            quantity={quantity}
                            onClose={onClose}
                            product={product}
                            comment={comment}
                        />
                    </div>
                </div>
            </div>

            {/* Open Comment Modal */}
            {showInput && (
                <div style={styles.overlay}>
                    <CommentModal
                        comment={comment}
                        setComment={setComment}
                        onClose={() => setShowInput(false)}
                        onSubmit={(newComment) => setComment(newComment)}
                        product={product}
                    />
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        padding: Spacing.SCALE_16,
        width: '100%',
        maxWidth: 540,
        borderRadius: Spacing.SCALE_12,
        backgroundColor: '#fff',
        position: 'relative',
        zIndex: 10,
        boxShadow: '0 12px 30px rgba(0, 0, 0, 0.2)',
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: "0.5em",
    },
    header: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    closeButton: {
        fontSize: 28,
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#888',
    },
    imageContainer: {
        flex: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: Spacing.SCALE_8,
        overflow: 'hidden',
    },
    image: {
        width: '100%',
        height: 'auto',
        minHeight: Spacing.SCALE_160,
        maxHeight: Spacing.SCALE_260,
        objectFit: 'contain',
        borderBottomLeftRadius: 5,
        borderTopLeftRadius: 5,
    },
    titlePriceRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: Spacing.SCALE_4,
    },
    title: {
        fontSize: 20,
        fontWeight: 'bold',
        margin: 0,
    },
    price: {
        fontSize: 18,
        fontWeight: 'bold',
        color: Colors.PRM_DARK_TEXT || '#333',
        margin: 0,
    },
    description: {
        fontSize: 16,
        color: '#666',
        marginBottom: Spacing.SCALE_12,
    },
    commentDisplay: {
        minHeight: 24,
        marginBottom: Spacing.SCALE_12,
        overflow: 'hidden',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
    },
    smallButton: {
        backgroundColor: Colors.PRM_LIGHT_BLUE,
        padding: '10px 16px',
        border: 'none',
        borderRadius: 6,
        color: 'white',
        fontWeight: 'bold',
        cursor: 'pointer',
        fontSize: 14,
        whiteSpace: 'nowrap',
    },
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10001,
    },
};

export default ProductModal;

